import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../../lib/cn.js';
/**
 * Default heuristic: length ≥ 8, mixed case, digit, symbol — one point each
 * (min 1 for any non-empty password). Swap in zxcvbn or a policy-aware
 * scorer via the `score` prop.
 */
export function defaultPasswordScore(password) {
    if (password.length === 0)
        return 0;
    let points = 0;
    if (password.length >= 8)
        points++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password))
        points++;
    if (/\d/.test(password))
        points++;
    if (/[^a-zA-Z0-9]/.test(password))
        points++;
    return Math.max(1, Math.min(points, 4));
}
const segmentTone = {
    1: 'bg-danger',
    2: 'bg-warn',
    3: 'bg-info',
    4: 'bg-pos',
};
const labelTone = {
    1: 'text-danger',
    2: 'text-warn',
    3: 'text-info',
    4: 'text-pos',
};
/**
 * PasswordStrength — segmented 4-bar meter + tone-colored label, injectable
 * scoring (research/design-system.md Tier 3, Auth screens). Announces via
 * `role="meter"`; the label updates in an `aria-live` region.
 */
export function PasswordStrength({ value, score = defaultPasswordScore, labels, label, className, ...props }) {
    const current = value.length === 0 ? 0 : score(value);
    return (_jsxs("div", { className: cn('flex flex-col gap-1.5', className), ...props, children: [_jsx("div", { role: "meter", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": 4, "aria-valuenow": current, "aria-valuetext": current === 0 ? undefined : labels[current - 1], className: "flex gap-1", children: [1, 2, 3, 4].map((segment) => (_jsx("span", { "data-part": "strength-segment", "data-filled": segment <= current || undefined, className: cn('h-1 flex-1 rounded-full transition-colors duration-150', segment <= current && current > 0 ? segmentTone[current] : 'bg-surface-3') }, segment))) }), _jsx("div", { "aria-live": "polite", className: "min-h-4 text-caption font-semibold", children: current === 0 ? null : (_jsx("span", { className: labelTone[current], children: labels[current - 1] })) })] }));
}
//# sourceMappingURL=PasswordStrength.js.map